// at the top of your file
import React, { useContext, useEffect, useState } from "react";
import {
  CButton,
  CCard,
  CCardHeader,
  CCol,
  CRow,
  CModal,
  CModalHeader,
  CModalBody,
  CModalFooter,
  CModalTitle,
  CSpinner,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import DownloadExcel from "src/reusables/DownloadExcel";
import { DataGrid } from "@mui/x-data-grid";
import formatter from "src/reusables/formatter";
import { getAll, getDetails } from "src/services/Visitations";
import { Toaster } from "src/components/Toaster";
import { format, isWeekend } from "date-fns";
import ls from "local-storage";
import Authentication, { AUTH_KEY } from "src/utils/auth/Authentication";
import { AuthContext } from "src/App";
import Moment from "moment";
import { DateRangePicker } from "react-date-range";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Select from "react-select";
import { getAll as getAllUsers } from "src/services/Users";
import VisitationSummary from "./components/VisitationSummary";
import DurationCards from "./components/DurationCards";
import { getDurationSummary } from "src/services/Visitations"; // your new API call
import { getSalesList } from "src/services/SMD";
import { usePaginationState } from "src/reusables/usePaginationState";

function Visitations() {
  const [dataRows, setDataRows] = useState([]);
  const [dataColumns, setDataColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [color, setColor] = useState("");
  const [refetch, setRefetch] = useState(false);
  const [renderCounter, setRenderCounter] = useState(0);
  const [drModal, setDrModal] = useState(false);
  const [durationSummary, setDurationSummary] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialStartDate = searchParams.get("startDate")
    ? new Date(searchParams.get("startDate"))
    : new Date(Moment(new Date()).startOf("month"));
  const initialEndDate = searchParams.get("endDate")
    ? new Date(searchParams.get("endDate"))
    : new Date();
  const initialSalesID = searchParams.get("salesID") || null;
  const initialDuration = searchParams.get("duration") || "";
  const initialVisibility = searchParams.get("visibility") || null;

  const useWindowDimensions = () => {
    const { innerWidth: width, innerHeight: height } = window;
    const comparison = width / height;
    const [windowDimensions, setWindowDimensions] = useState({
      width,
      height,
      comparison,
    });

    useEffect(() => {
      function handleResize() {
        const { innerWidth: width, innerHeight: height } = window;
        const comparison = width / height;
        setWindowDimensions({ width, height, comparison });
      }

      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);

    return windowDimensions;
  };

  const { height, width, comparison } = useWindowDimensions();

  function customDayContent(day) {
    let extraDot = null;
    if (isWeekend(day)) {
      extraDot = (
        <div
          style={{
            height: "5px",
            width: "5px",
            borderRadius: "100%",
            background: "orange",
            position: "absolute",
            top: 2,
            right: 2,
          }}
        />
      );
    }
    return (
      <div>
        <span
          style={{
            fontWeight: "600",
          }}
        >
          {format(day, "d")}
        </span>
      </div>
    );
  }
  const [paginationModel, setPaginationModel] = usePaginationState();
  const [dateRange, setDateRange] = useState([
    { startDate: initialStartDate, endDate: initialEndDate },
  ]);
  const [salesOptions, setSalesOptions] = useState([]);
  const [selectedSales, setSelectedSales] = useState(null);
  const [loadingSales, setLoadingSales] = useState(false);
  const [durationFilter, setDurationFilter] = useState(initialDuration);
  const [visibilityFilter, setVisibilityFilter] = useState(initialVisibility);

  const durationOptions = [
    { value: "", label: "Semua Durasi" },
    { value: "<15", label: "< 15 Menit" },
    { value: "15-30", label: "15 - 30 Menit" },
    { value: ">30", label: "> 30 Menit" },
  ];

  const visibilityOptions = [
    { value: "", label: "All Visibility" },
    { value: "Low", label: "Low" },
    { value: "Medium", label: "Medium" },
    { value: "High", label: "High" },
  ];

  useEffect(() => {
    const fetchSalesPeople = async () => {
      setLoadingSales(true);
      try {
        const res = await getSalesList();
        const options = res.data.map((sales) => ({
          value: sales.id,
          label: sales.name,
        }));

        setSalesOptions(options);

        if (initialSalesID) {
          const parsed = parseInt(initialSalesID);
          const matched = options.find((opt) => opt.value === parsed);
          if (matched) setSelectedSales(matched);
        }
      } catch {
        setMessage("Gagal memuat SMD");
        setColor("danger");
        setOpen(true);
      } finally {
        setLoadingSales(false);
      }
    };

    fetchSalesPeople();
  }, []);

  const fetchDurationSummary = async () => {
    const dateFrom = formatter("SQLDate", dateRange[0].startDate);
    const dateTo = formatter("SQLDate", dateRange[0].endDate);
    const smdID = selectedSales?.value || null;

    try {
      const res = await getDurationSummary({ dateFrom, dateTo, smdID });
      setDurationSummary(res.data || []);
    } catch {
      setMessage("Gagal memuat ringkasan durasi");
      setColor("danger");
      setOpen(true);
    }
  };

  const fetchMainData = async ({
    dateFrom,
    dateTo,
    userID,
    duration,
    page,
    pageSize,
    visibility,
  }) => {
    setLoading(true);
    try {
      fetchDurationSummary();
      const res = await getAll({
        dateFrom,
        dateTo,
        smdID: userID,
        duration,
        page,
        pageSize,
        visibility,
      });
      const rows = res.visitations.map((item, i) => ({
        no: i + 1,
        id: item.id,
        name: item.userName,
        storeName: item.storeName,
        checkIn: item.check_in,
        checkOut: item.check_out ?? "Belum",
        visibility: item.visibility,
        duration: item.duration,
        date: item.date,
        rawDate: item.created_at,
      }));
      setDataRows(rows);
      setDataColumns([
        { field: "no", headerName: "No", width: 60 },
        { field: "date", headerName: "Tanggal", flex: 1 },
        { field: "name", headerName: "Nama", flex: 1 },
        { field: "storeName", headerName: "Toko", flex: 1 },
        { field: "checkIn", headerName: "Check In", flex: 1 },
        { field: "checkOut", headerName: "Check Out", flex: 1 },
        { field: "duration", headerName: "Durasi (Menit)", flex: 1 },
        { field: "visibility", headerName: "Visibility", flex: 1 },
      ]);
    } catch {
      setMessage("Gagal memuat data kunjungan");
      setColor("danger");
      setOpen(true);
      setDataRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Remove the old useEffect that used refetch/renderCounter/page/pageSize for data fetching

  // --- Single source of truth: refetch on any relevant change (filters, pagination, etc.) ---
  useEffect(() => {
    const page = parseInt(searchParams.get("page")) || 0;
    const pageSize = parseInt(searchParams.get("pageSize")) || 10;
    const newStart = formatter("SQLDate", dateRange[0].startDate);
    const newEnd = formatter("SQLDate", dateRange[0].endDate);
    const salesID = selectedSales?.value || null;
    fetchMainData({
      dateFrom: newStart,
      dateTo: newEnd,
      userID: salesID,
      duration: durationFilter,
      page,
      pageSize,
      visibility: visibilityFilter,
    });
    // Add all dependencies that affect the data
  }, [
    dateRange,
    drModal,
    selectedSales,
    durationFilter,
    visibilityFilter,
    searchParams, // this covers pagination changes
  ]);

  function handleRowClick(params) {
    navigate(`/visitations/details/${params.row.id}`);
  }

  // Helper function to update URL parameters
  const updateUrlParams = (updates) => {
    const newSearchParams = new URLSearchParams(searchParams);
    
    // Update each parameter
    Object.entries(updates).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        newSearchParams.set(key, value);
      } else {
        newSearchParams.delete(key);
      }
    });
    
    setSearchParams(newSearchParams);
  };

  // Update date range handler
  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
    updateUrlParams({
      startDate: formatter("SQLDate", newDateRange[0].startDate),
      endDate: formatter("SQLDate", newDateRange[0].endDate)
    });
  };

  // Update sales selection handler
  const handleSalesChange = (selectedOption) => {
    setSelectedSales(selectedOption);
    updateUrlParams({
      salesID: selectedOption?.value || null
    });
  };

  // Update duration filter handler
  const handleDurationChange = (selectedOption) => {
    setDurationFilter(selectedOption.value);
    updateUrlParams({
      duration: selectedOption.value || null
    });
  };

  // Update visibility filter handler
  const handleVisibilityChange = (selectedOption) => {
    setVisibilityFilter(selectedOption.value);
    updateUrlParams({
      visibility: selectedOption.value || null
    });
  };

  return (
    <div style={{ color: "black" }}>
      <CModal
        visible={drModal}
      >
        <CModalHeader>
          <CModalTitle>Date Range</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <CCol xs="12" lg="10">
            <DateRangePicker
              onChange={(item) => {
                setDateRange([item.range1]);
                ls.set("dateRange", item.range1);
              }}
              showSelectionPreview={true}
              moveRangeOnFirstSelection={false}
              months={1}
              maxDate={initialEndDate}
              ranges={dateRange}
              direction="vertical"
              weekStartsOn={1}
              calendarFocus="backwards"
              preventSnapRefocus={true}
              rangeColors={["#4E5B97"]}
              dayContentRenderer={customDayContent}
            />
          </CCol>
        </CModalBody>
        <CModalFooter
          style={{
            width: comparison < 1 ? `${(85 * 1) / comparison}vw` : "",
          }}
        >
          <CButton
            style={{ backgroundColor: "#4E5B97", border: "none" }}
            onClick={() => setDrModal(!drModal)}
          >
            Close
          </CButton>
          <CButton
            style={{ backgroundColor: "#4E5B97", border: "none" }}
            onClick={() => {
              handleDateRangeChange(dateRange); // Use the new handler
              setDrModal(false);
            }}
          >
            OK
          </CButton>
        </CModalFooter>
      </CModal>
      <Toaster open={open} message={message} setOpen={setOpen} color={color} />
      <CCard>
        <CCardHeader>
          <CRow className="mb-3">
            <CCol xs={12} lg={7}>
              <h4>Daftar Kunjungan</h4>
            </CCol>
            <CCol xs={12} lg={5} className="text-end">
              <DownloadExcel
                rows={dataRows}
                columns={dataColumns}
                excelName="Daftar Visitations"
                disabled={loading || dataRows.length === 0}
              />
            </CCol>
          </CRow>
          <DurationCards
            groupedData={durationSummary}
            currentFilter={durationFilter}
            onClick={(d) => {
              const selectedOption = durationOptions.find(opt => opt.value === d);
              handleDurationChange(selectedOption); // Use the new handler
            }}
          />

          <CRow className="mb-2">
            <CCol md={6}>
              <CButton
                style={{ backgroundColor: "#306fba", border: "none" }}
                onClick={() => setDrModal(!drModal)}
              >
                {Moment(dateRange[0].startDate).format("ll")} -{" "}
                {Moment(dateRange[0].endDate).format("ll")}
              </CButton>
            </CCol>
            <CCol md={6}>
              <Select
                options={salesOptions}
                value={selectedSales}
                onChange={handleSalesChange} // Updated handler
                placeholder="Pilih SMD"
                isClearable
                isDisabled={loadingSales}
              />
            </CCol>
          </CRow>
          <CRow className="mb-2">
            <CCol md={6}>
              <Select
                options={durationOptions}
                value={durationOptions.find(
                  (opt) => opt.value === durationFilter
                )}
                onChange={handleDurationChange} // Updated handler
                placeholder="Filter Durasi"
              />
            </CCol>
            <CCol md={6}>
              <Select
                options={visibilityOptions}
                value={visibilityOptions.find(
                  (opt) => opt.value === visibilityFilter
                )}
                onChange={handleVisibilityChange} // Updated handler
                placeholder="Filter Visibility"
              />
            </CCol>
          </CRow>
        </CCardHeader>

        <div style={{ margin: "16px", position: "relative" }}>
          {loading && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                zIndex: 1000,
              }}
            >
              <div style={{ textAlign: "center" }}>
                <CSpinner size="lg" />
                <div>Loading visitations...</div>
              </div>
            </div>
          )}
          <VisitationSummary
            data={dataRows}
            loading={loading}
            onFilterByDuration={(duration) => {
              setDurationFilter(duration);
            }}
          />

          <DataGrid
            rows={dataRows}
            columns={dataColumns}
            onRowClick={handleRowClick}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 20, 25]}
            autoHeight
            disableRowSelectionOnClick
            loading={loading}
            sx={{
              "& .MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-columnHeader:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-root": {
                fontSize: window.innerWidth < 768 ? "0.875rem" : "1rem",
              },
              "& .MuiDataGrid-cell": {
                padding: window.innerWidth < 768 ? "8px 4px" : "8px 16px",
              },
              "& .MuiDataGrid-columnHeader": {
                padding: window.innerWidth < 768 ? "8px 4px" : "8px 16px",
              },
            }}
          />
        </div>
      </CCard>
    </div>
  );
}

export default Visitations;
